import React, { useEffect, useRef } from 'react';
import './HomeAddressFields.css';
import useGeneralDelivery from '../../../../../../../hooks/useGeneralDelivery';
import { usePageProperties } from '../../../../../providers/PagePropertiesProvider';
import { THomeAddress, useHomeAddress } from '../../../../../providers/HomeAddressProvider';
import { usePersonalInformation } from '../../../../../providers/PersonalInformationProvider';
import { useHomeAddressError } from '../../../../../providers/HomeAddressErrorProvider';
import HomeAddressInput from '../HomeAddressInput/HomeAddressInput';
import AutoFillFocusProvider from '../../../../../providers/AutoFillFocusProvider';
import HomeAddressStateDropdown from '../HomeAddressStateDropdown/HomeAddressStateDropdown';
import { namesAndIds } from '../../../../../../../types/shared/PrequalificationDataEntry';
import ApartmentField from '../../../../../../FieldContainers/AddressFields/ApartmentField/ApartmentField';
import CityField from '../../../../../../FieldContainers/AddressFields/CityField/CityField';
import ZipCodeField from '../../../../../../FieldContainers/AddressFields/ZipCodeField/ZipCodeField';
import { regexValidation, wrapTrademarkSuperscript } from '../../../../../../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../../../../../../utils/rewriteLink/rewriteLink';
import useLoqate from '../../../../../../../hooks/useLoqate';
import { generateFieldErrorMessage } from '../../../../../../../utils/error-message';
import { Button, DynamicContainer } from '../../../../../../../importCL';
import type { TCheckRegex } from '../../Inputs.types';

export type THomeAddressFieldsProps = {
    addressValidationCheck: () => void;
    checkRegex: TCheckRegex;
    checkAptUnitPoBox: (aptUnit: string, type: string) => void
    prefill: boolean;
    isLocked: boolean;
    onReEnter: () => void;
    reEnterAddressCtaText: string;
}

export default function HomeAddressFields({ addressValidationCheck, checkRegex, checkAptUnitPoBox, prefill, isLocked, onReEnter, reEnterAddressCtaText }: THomeAddressFieldsProps) {
    const fieldsReset = useRef<boolean>(false);
    const regexContainer = regexValidation();
    const { mailingAddressNote } = usePageProperties();
    const { personalInformation: { MailingAddressLine1, MailingAddressLine2 } } = usePersonalInformation();
    const { showHomeAddress, setShowHomeAddress, homeAddress, setSingleValue, batchSetValues } = useHomeAddress();
    const { homeAddressError, dispatch } = useHomeAddressError();
    const { matchGeneralDelivery } = useGeneralDelivery();
    const { aptUnitPoBox } = usePageProperties();
    const matchesGeneralDelivery = aptUnitPoBox ? matchGeneralDelivery(MailingAddressLine2) || matchGeneralDelivery(MailingAddressLine1) : matchGeneralDelivery(MailingAddressLine1);
    const { loqateEnabled } = useLoqate();

    useEffect(() => {
        if (!showHomeAddress) {
            dispatch({
                type: 'batchSetValid',
                payload: {
                    HomeAddressLine1: {
                        isInError: false,
                        isValidValue: true,
                        required: true,
                    },
                    HomeAddressLine2: {
                        isInError: false,
                        isValidValue: true,
                        required: false,
                    },
                    HomeCity: {
                        isInError: false,
                        isValidValue: true,
                        required: true,
                    },
                    HomeState: {
                        isInError: false,
                        isValidValue: true,
                        required: true,
                    },
                    HomeZipCode: {
                        isInError: false,
                        isValidValue: true,
                        required: true,
                    },
                },
            });
        }
    }, [showHomeAddress]);

    /**
     * Resets the Home Address Fields when they are not in use.
     */
    useEffect(() => {
        if (loqateEnabled) {
            return;
        }

        setShowHomeAddress(matchesGeneralDelivery);

        if (!matchesGeneralDelivery && fieldsReset.current) {
            batchSetValues({
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeZipCode: '',
            });
            fieldsReset.current = false;
        } else if (!fieldsReset.current) {
            fieldsReset.current = true;
        }
    }, [MailingAddressLine1]);

    const trimSpace = (keyName: keyof THomeAddress, value: string) => {
        setSingleValue(keyName)(value.trim());
    };

    return !showHomeAddress ? <></> : (
        <>
            <div className='Pqde-HomeAddressFields'>
                <p className='Pqde-HomeAddressFields__disclaimer'>
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(mailingAddressNote))}
                </p>
                <DynamicContainer displayTag={true} tag='div' className='Row'>
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-XS-6 COL-MD-3'>
                        <AutoFillFocusProvider>
                            <div id='homeAddress-field-container'>
                                <HomeAddressInput
                                    checkRegex={checkRegex}
                                    addressValidationCheck={addressValidationCheck}
                                    disabled={isLocked} />
                            </div>
                        </AutoFillFocusProvider>
                    </DynamicContainer>
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-XS-6 COL-MD-2'>
                        <ApartmentField
                            {...namesAndIds.HomeAddressLine2}
                            value={homeAddress.HomeAddressLine2}
                            setValueCallback={(value: string) => {
                                const containsInvalidCharacters = regexContainer.disallow.invalid_address_characters.test(value);

                                if (containsInvalidCharacters) {
                                    value = value.replace(regexContainer.disallow.invalid_address_characters, '');
                                }

                                setSingleValue('HomeAddressLine2')(value);
                            }}
                            error={homeAddressError.HomeAddressLine2.isInError}
                            valid={[homeAddressError.HomeAddressLine2.isValidValue, (isValid) => {
                                dispatch({ type: 'setValid', target: 'HomeAddressLine2', payload: isValid });
                            }]}
                            onBlurCallback={() => {
                                addressValidationCheck();
                                checkAptUnitPoBox(homeAddress.HomeAddressLine2, 'Home');
                            }}
                            disabled={isLocked}
                            useSuccessIcon
                            showSuccessIcon={isLocked ? homeAddress.HomeAddressLine2 !== '' : undefined}
                            addressType='home'
                            isPrefill={prefill}
                        />
                    </DynamicContainer>
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-XS-6 COL-MD-2'>
                        <div id='homeCity-field-container'>
                            <CityField
                                required alternate
                                {...namesAndIds.HomeCity}
                                value={homeAddress.HomeCity}
                                setValueCallback={(val: string) => {
                                    setSingleValue('HomeCity')(val);
                                    dispatch({ type: 'setValid', target: 'HomeCity', payload: true });
                                }}
                                error={homeAddressError.HomeCity.isInError}
                                valid={[homeAddressError.HomeCity.isValidValue, (isValid) => {
                                    dispatch({ type: 'setValid', target: 'HomeCity', payload: isValid });
                                }]}
                                requiredMessage={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                errorText={generateFieldErrorMessage('Home Address City', 'missingValue')}
                                onBlurCallback={() => {
                                    trimSpace('HomeCity', homeAddress.HomeCity);
                                    addressValidationCheck();
                                    checkRegex('Home', 'city', homeAddress.HomeCity, undefined, 'HomeCity');
                                }}
                                onKeyDownCallback={(e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        trimSpace('HomeCity', homeAddress.HomeCity);
                                        addressValidationCheck();
                                        checkRegex('Home', 'city', homeAddress.HomeCity, undefined, 'HomeCity');
                                    }
                                }}
                                disabled={isLocked}
                                useOutsideValidation={homeAddressError.HomeCity.isValidValue}
                                useSuccessIcon
                                showSuccessIcon={isLocked || undefined}
                                isPrefill={prefill}
                            />
                        </div>
                    </DynamicContainer>
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-XS-6 COL-MD-2'>
                        <HomeAddressStateDropdown
                            key={isLocked ? 'home-locked' : 'home-editable'}
                            addressValidationCheck={addressValidationCheck}
                            isPrefill={prefill}
                            disabled={isLocked}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    addressValidationCheck();
                                }
                            }} />
                    </DynamicContainer>
                    <DynamicContainer displayTag={true} tag='div' className='Col COL-XS-6 COL-MD-2'>
                        <div id='homeZipCode-field-container'>
                            <ZipCodeField
                                errorText={generateFieldErrorMessage('Home Address ZIP Code', 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('Home Address ZIP Code')}
                                requiredMessage={generateFieldErrorMessage('Home Address ZIP Code', 'missingValue')}
                                required alternate
                                {...namesAndIds.HomeZipCode}
                                value={homeAddress.HomeZipCode}
                                setValueCallback={(val: string) => {
                                    setSingleValue('HomeZipCode')(val);
                                    dispatch({ type: 'setValid', target: 'HomeZipCode', payload: true });
                                }}
                                error={homeAddressError.HomeZipCode.isInError}
                                valid={[homeAddressError.HomeZipCode.isValidValue, (isValid) => {
                                    dispatch({ type: 'setValid', target: 'HomeZipCode', payload: isValid });
                                }]}
                                onBlurCallback={() => {
                                    if (homeAddress.HomeZipCode.length === 5) {
                                        addressValidationCheck();
                                    }
                                }}
                                onKeyDownCallback={(e) => {
                                    if (e.key === 'Enter') {
                                        addressValidationCheck();
                                    }
                                }}
                                disabled={isLocked}
                                useOutsideValidation={homeAddressError.HomeZipCode.isValidValue}
                                isPrefill={prefill}
                                useSuccessIcon
                                showSuccessIcon={isLocked || undefined}
                            />
                        </div>
                    </DynamicContainer>
                </DynamicContainer>
                {isLocked &&
                    <DynamicContainer displayTag={true} tag='div' className='Row'>
                        <DynamicContainer displayTag={true} tag='div' className='Col'>
                            <Button
                                variant='link'
                                size='small'
                                className='Pqde-HomeAddressFields__reEnterCta'
                                aria-label={`${reEnterAddressCtaText} for Home Address`}
                                onClick={onReEnter}
                            >
                                {reEnterAddressCtaText}
                            </Button>
                        </DynamicContainer>
                    </DynamicContainer>
                }
            </div>
            <hr />
        </>
    );
}
